import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

function Partners() {
  const [partners, setPartners] = useState([]);
  // const [partners, setPartners] = useState([]); // Removed duplicate
  const [selectedPartner, setSelectedPartner] = useState(null); // For rejection or specific actions
  const [viewPartner, setViewPartner] = useState(null); // For detailed view
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [isLoading, setIsLoading] = useState(true);

  // States restored
  const [actionLoading, setActionLoading] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const [rejectionReason, setRejectionReason] = useState('');

  // Create Partner State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPartner, setNewPartner] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    password: '',
    status: 'approved' // Admin created partners are approved by default
  });
  const [showPassword, setShowPassword] = useState(false);

  const location = useLocation();
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setIsCreateModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch Partners
  useEffect(() => {
    const q = query(collection(db, 'partners'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const partnersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPartners(partnersData);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching partners:", error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Approval Logic
  const approvePartner = async (partner) => {
    if (!window.confirm(`Are you sure you want to approve ${partner.name}?`)) return;

    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'partners', partner.id), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: 'admin'
      });

      // Notification
      await addDoc(collection(db, 'notifications'), {
        userId: partner.id,
        type: 'approval',
        title: 'Account Approved!',
        message: 'Your partner account has been approved. You can now access all features.',
        read: false,
        createdAt: serverTimestamp()
      });

      alert("Partner approved successfully!");
    } catch (error) {
      console.error("Error approving partner:", error);
      alert("Failed to approve partner");
    } finally {
      setActionLoading(false);
    }
  };

  // Rejection Logic
  const handleRejectClick = (partner) => {
    setSelectedPartner(partner);
    setIsRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      alert("Please enter a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'partners', selectedPartner.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: 'admin',
        rejectionReason: rejectionReason.trim()
      });

      // Notification
      await addDoc(collection(db, 'notifications'), {
        userId: selectedPartner.id,
        type: 'rejection',
        title: 'Account Not Approved',
        message: `Your partner account application was not approved. Reason: ${rejectionReason}`,
        read: false,
        createdAt: serverTimestamp()
      });

      alert("Partner rejected.");
      setIsRejectModalOpen(false);
      setRejectionReason('');
      setSelectedPartner(null);
    } catch (error) {
      console.error("Error rejecting partner:", error);
      alert("Failed to reject partner");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this partner? This cannot be undone.")) {
      try {
        await deleteDoc(doc(db, 'partners', id));
      } catch (err) {
        console.error("Error deleting partner:", err);
        alert("Failed to delete partner");
      }
    }
  };

  const filteredPartners = partners.filter(p => {
    const matchesSearch = (p.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (p.company?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (p.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <AdminSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <div className="flex items-center">
                {isMobile && (
                  <button
                    onClick={toggleMobileSidebar}
                    className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
                <h1 className="text-3xl font-bold text-gray-800">Partners</h1>
              </div>
              <p className="text-gray-500 mt-1">Manage partner applications and accounts</p>
            </div>
            {/* Normally partners sign up themselves via app, but if admin needs to create, we'd add button here */}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'all' ? 'bg-brand-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
              >
                All Partners
              </button>
              <button
                onClick={() => setFilterStatus('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilterStatus('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'approved' ? 'bg-green-100 text-green-800' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
              >
                Approved
              </button>
              <button
                onClick={() => setFilterStatus('rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
              >
                Rejected
              </button>
            </div>

            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-brand-golden text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Partner
            </button>
          </div>
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search partners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-golden"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Partner / Company</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Location</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPartners.map((partner) => (
                <tr
                  key={partner.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setViewPartner(partner)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-golden/20 flex items-center justify-center text-brand-dark font-bold">
                        {partner.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{partner.name}</div>
                        <div className="text-xs text-gray-500">{partner.company}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{partner.email}</div>
                    <div className="text-xs text-gray-500">{partner.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{partner.location}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(partner.status)}`}>
                      {partner.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      {/* Actions for Pending Partners */}
                      {partner.status === 'pending' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); approvePartner(partner); }}
                            className="p-1 rounded hover:bg-green-100 text-green-600"
                            title="Approve"
                            disabled={actionLoading}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRejectClick(partner); }}
                            className="p-1 rounded hover:bg-red-100 text-red-600"
                            title="Reject"
                            disabled={actionLoading}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(partner.id); }}
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredPartners.length === 0 && !isLoading && (
            <div className="p-8 text-center text-gray-500">No partners found.</div>
          )}
        </div>
      </div>


      {/* Reject Modal */}
      {
        isRejectModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setIsRejectModalOpen(false)}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4 text-red-600">Reject Partner</h2>
              <p className="text-gray-600 mb-4">Please provide a reason for rejecting {selectedPartner?.name}.</p>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 h-32 mb-4 focus:ring-2 focus:ring-red-500 focus:outline-none"
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setIsRejectModalOpen(false); setRejectionReason(''); setSelectedPartner(null); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejection}
                  disabled={actionLoading}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Partner Details Modal */}
      {
        viewPartner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewPartner(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="relative bg-brand-dark h-32">
                <button
                  onClick={() => setViewPartner(null)}
                  className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-8 pb-8">
                <div className="relative -mt-16 mb-6">
                  <div className="w-32 h-32 rounded-full border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center">
                    {viewPartner.profileImageUrl ? (
                      <img
                        src={viewPartner.profileImageUrl}
                        alt={viewPartner.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-4xl font-bold text-gray-400">
                        {viewPartner.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{viewPartner.name}</h2>
                  <p className="text-gray-500 font-medium">{viewPartner.company}</p>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(viewPartner.status)}`}>
                      {viewPartner.status?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact Information</label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center text-gray-600">
                          <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {viewPartner.email}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {viewPartner.phone}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Company Details</label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center text-gray-600">
                          <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {viewPartner.location}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )
      }
      {/* Create Partner Modal */}
      {
        isCreateModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setIsCreateModalOpen(false)}>
            <div className="bg-white rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Partner</h2>
                <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                setActionLoading(true);

                try {
                  // Use a secondary app to avoid logging out the admin
                  let secondaryApp;
                  if (getApps().length > 1) {
                    secondaryApp = getApps().find(app => app.name === 'Secondary');
                    if (!secondaryApp) secondaryApp = initializeApp(getApp().options, 'Secondary');
                  } else {
                    secondaryApp = initializeApp(getApp().options, 'Secondary');
                  }

                  const secondaryAuth = getAuth(secondaryApp);
                  const { email, password, name, phone, company, location } = newPartner;

                  // Create User in Auth
                  const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
                  const user = userCredential.user;

                  // Save to Firestore
                  await addDoc(collection(db, 'partners'), {
                    name: name.trim(),
                    email: email.trim().toLowerCase(),
                    phone: phone.trim(),
                    company: company.trim(),
                    location: location.trim(),
                    status: 'approved',
                    userId: user.uid,
                    role: 'partner',
                    createdAt: serverTimestamp(),
                    createdBy: 'admin'
                  });

                  setIsCreateModalOpen(false);
                  setNewPartner({ name: '', email: '', phone: '', company: '', location: '', password: '', status: 'approved' });
                  alert('Partner added successfully!');
                } catch (error) {
                  console.error("Error creating partner:", error);
                  alert("Failed to create partner: " + error.message);
                } finally {
                  setActionLoading(false);
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                      value={newPartner.name}
                      onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                      value={newPartner.company}
                      onChange={(e) => setNewPartner({ ...newPartner, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                      value={newPartner.email}
                      onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                    />
                  </div>


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        minLength={6}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent pr-10"
                        value={newPartner.password}
                        onChange={(e) => setNewPartner({ ...newPartner, password: e.target.value })}
                        placeholder="Min. 6 characters"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.05 10.05 0 011.574-2.59M5.312 5.312l13.376 13.376M17.842 9.92c.622.95 1.05 2.003 1.258 3.12l-1.636 1.636m-1.92 1.92l-5.636-5.636m-2.42-2.42l5.636 5.636M12 12a3 3 0 000 6 3 3 0 000-6z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                      value={newPartner.phone}
                      onChange={(e) => setNewPartner({ ...newPartner, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location/Address</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                      value={newPartner.location}
                      onChange={(e) => setNewPartner({ ...newPartner, location: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="px-4 py-2 text-white bg-brand-golden rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                  >
                    {actionLoading ? 'Creating...' : 'Create Partner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default Partners;